import { RegularViewTableItemData } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/useRegularViewTableController.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { normalizeScreamingSnakeToUI } from '@dealstack/shared/lib/format/string';
import {
	DataMipTct,
	DataSecurityTct,
	GetInvestmentProgrammeByPersonIdRes,
	GetPersonForCapRes,
	GetSecurityEventsRes,
	HoldingEventRes,
	HoldingEventType,
	HoldingSecurityRes,
	MipState,
	Receiver,
	SecurityEventRes,
	SecurityEventType,
	SecurityType,
	TransactionThresholdExpressedAs,
	TransactionType,
} from '@dealstack/shared/lib/generated';
import {
	GENERIC_ELEMENT_ID,
	makeComplexDataTestId,
	TEST_ELEMENT_IDS,
} from '@dealstack/shared/tests/testElementIds.ts';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

interface ExtendedSecurityEventRes extends SecurityEventRes {
	postEventSecurity?: {
		name: string;
	};
}

export const extractCountryCodeFromJurisdictionCountryCode = (jurisdictionCountryCode?: string) =>
	jurisdictionCountryCode?.split('-')[0];

export const getSecuritiesOptionsForSelect = (securitiesNames?: DataSecurityTct[]) => {
	return (
		securitiesNames
			?.map(({ id, name }) => ({ label: name, value: id }))
			.sort((a, b) => a.label.localeCompare(b.label)) || []
	);
};

export const getHoldingsOptionsForSelect = (securitiesNames?: HoldingSecurityRes[]) => {
	return (
		securitiesNames
			?.map(({ name }) => ({ label: name, value: name }))
			.sort((a, b) => a.label.localeCompare(b.label)) || []
	);
};

export const getPersonsLabelsWithIdsForSelect = (persons?: GetPersonForCapRes[]) => {
	return (
		persons
			?.map(({ id, computedName }) => ({
				label: computedName ?? '',
				value: computedName,
				id,
			}))
			.sort((a, b) => a.label.localeCompare(b.label)) || []
	);
};

export const getNewPersonsOptionsForSelect = (persons?: GetPersonForCapRes[]) => {
	return (
		persons?.map(
			({ id, computedName, type, jurisdictionCountryCode, corporateRegistrationNumber }) => ({
				value: id,
				label: computedName ?? '',
				name: computedName ?? '',
				type,
				corporateRegistrationNumber,
				countryCode: jurisdictionCountryCode,
				dataTestId: makeComplexDataTestId(GENERIC_ELEMENT_ID.ENTITY_SELECT, computedName),
			})
		) || []
	);
};

const getLabel = (
	rest: Omit<DataMipTct | Receiver | GetInvestmentProgrammeByPersonIdRes, 'id'>
): string => {
	if ('programmeName' in rest) {
		return (rest as { programmeName: string }).programmeName;
	}
	if ('name' in rest) {
		return (rest as { name: string }).name;
	}
	return '';
};

export const getMipOptionsForSelect = (
	mips?: Array<DataMipTct | Receiver | GetInvestmentProgrammeByPersonIdRes>
) => {
	return (
		mips
			?.filter((props) => ('state' in props ? props.state === MipState.ACTIVE : true))
			.map(({ id, ...rest }) => ({
				label: getLabel(rest) || '',
				value: id || '',
			})) || []
	);
};

export const TRANSACTION_TYPE_OPTIONS = Object.values(TransactionType)
	.filter(
		(type) =>
			type !== TransactionType.ISSUE_SPLIT &&
			type !== TransactionType.REDEMPTION_REVERSE_SPLIT &&
			type !== TransactionType.TRANSFER_CONVERSION
	)
	.map((type) => ({
		value: type,
		label:
			type === TransactionType.CONVERSION_SHARE_CAPITAL
				? 'Transfer of Share Capital'
				: normalizeScreamingSnakeToUI(type),
		'data-testid': makeComplexDataTestId(
			TEST_ELEMENT_IDS.CAP_TRANSACTION_BULK_UPDATE_TYPE_SELECT,
			type
		),
	}));

export const TRANSACTION_PARTICIPATION_THRESHOLD_EXPRESSED_AS_OPTIONS = Object.values(
	TransactionThresholdExpressedAs
).map((type) => ({
	value: type,
	label: normalizeScreamingSnakeToUI(type),
}));

export const makeSecurityTypeName = (type: SecurityType) => {
	if (!type) {
		return '';
	}

	if (type === SecurityType.OTHER) {
		return 'Other (debt)';
	}

	if (type === SecurityType.OTHER_EQUITY) {
		return 'Other (equity)';
	}

	return normalizeScreamingSnakeToUI(type);
};

export const SECURITY_TYPE_OPTIONS = Object.values(SecurityType).map((type) => {
	return {
		value: type,
		label: makeSecurityTypeName(type),
	};
});

export const SECURITY_EVENT_TYPE_OPTIONS = Object.values(SecurityEventType).map((type) => {
	const label =
		type === 'ADJUST' ? 'Adjustment to nominal value' : normalizeScreamingSnakeToUI(type);

	return {
		value: type,
		label,
	};
});

export const HOLDINGS_EVENT_TYPE_OPTIONS = Object.values(HoldingEventType).map((type) => {
	const labelMap: Record<HoldingEventType, string> = {
		[HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES]: 'Issue certificate',
		[HoldingEventType.PLEDGING_OF_SHARES]: 'Pledge',
		[HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS]: 'Assignment',
		[HoldingEventType.NOTE]: 'Note',
	};

	return {
		value: type,
		label: labelMap[type] || normalizeScreamingSnakeToUI(type),
	};
});

export const formatNominalValue = (value?: number) => {
	if (!value || isNaN(value)) {
		return '-';
	}

	if (Math.abs(value) < 0.000001) {
		return '-';
	}

	return value.toFixed(5);
};

type EventDescriptionsDict = Record<string, string>;

interface GenerateEventDescriptionsParams {
	security?: DataSecurityTct;
	securityEvents?: GetSecurityEventsRes;
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>;
}

export function generateEventDescriptions({
	security,
	securityEvents,
	tCapManage,
}: GenerateEventDescriptionsParams): EventDescriptionsDict {
	if (!security || !securityEvents?.securityEvents?.length) {
		return {};
	}

	const sortedEvents = [...securityEvents.securityEvents].sort(
		(a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
	);

	const descriptions: EventDescriptionsDict = {};

	sortedEvents.forEach((ev: ExtendedSecurityEventRes) => {
		const oldNominal = ev.preEventNominalValue || security.nominalValue || 0;
		const newNominal = ev.postEventNominalValue || oldNominal;

		const eventDate = new Date(ev.date);
		const formattedDate = formatBEDateToUiWithFallback(ev.date, DATE_FORMATS.CALENDAR_DATE);

		const isFuture = eventDate > new Date();

		const becomeVerb = isFuture
			? tCapManage('securities.details.text_will_become')
			: tCapManage('securities.details.text_become');
		const changeVerb = isFuture
			? tCapManage('securities.details.text_will_change')
			: tCapManage('securities.details.text_change');

		let targetSecurityName = '';
		if (ev.type === SecurityEventType.CONVERSION && ev.postEventSecurity) {
			targetSecurityName = ev.postEventSecurity.name;
		}

		let description = '';
		switch (ev.type) {
			case SecurityEventType.SPLIT:
				description = `${tCapManage('securities.details.text_on')} ${formattedDate} ${tCapManage('securities.details.text_one_unit_of')} ${
					security.name
				} ${becomeVerb} ${ev.postEventUnits} ${tCapManage('securities.details.text_units_split')} ${changeVerb} ${tCapManage('securities.details.text_from')} ${
					oldNominal
				} ${tCapManage('securities.details.text_to')} ${newNominal}`;
				break;

			case SecurityEventType.REVERSE_SPLIT:
				description = `${tCapManage('securities.details.text_on')} ${formattedDate}, ${
					ev.preEventUnits
				} ${tCapManage('securities.details.text_units_of')} ${security.name} ${becomeVerb} ${tCapManage('securities.details.text_units_reverse')} ${changeVerb} ${tCapManage('securities.details.text_from')} ${
					oldNominal
				} ${tCapManage('securities.details.text_to')} ${newNominal}`;
				break;

			case SecurityEventType.ADJUST:
				description = `${tCapManage('securities.details.text_on')} ${formattedDate} ${tCapManage('securities.details.text_units_adjust')} ${
					security.name
				} ${changeVerb} ${tCapManage('securities.details.text_from')} ${oldNominal} ${tCapManage('securities.details.text_to')} ${newNominal}`;
				break;

			case SecurityEventType.CONVERSION:
				description = `${tCapManage('securities.details.text_on')} ${formattedDate} ${tCapManage('securities.details.text_one_unit_of')} ${
					security.name
				} ${becomeVerb}  ${tCapManage('securities.details.text_one_unit_of')} ${targetSecurityName}`;
				break;

			default:
				description = '';
				break;
		}

		descriptions[ev.id] = description;
	});

	return descriptions;
}

export const getEventTypeDisplayName = (
	eventType: HoldingEventType,
	t: TranslateFunction<I18Namespaces.CAP_MANAGE>
): string => {
	switch (eventType) {
		case HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES:
			return t('holdings.event.issue');
		case HoldingEventType.PLEDGING_OF_SHARES:
			return t('holdings.event.pledge');
		case HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS:
			return t('holdings.event.assigment');
		case HoldingEventType.NOTE:
			return t('holdings.event.note');
		default:
			return t('holdings.events.issue');
	}
};

export const generateHoldingEventDescription = (
	t: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	event?: HoldingEventRes,
	holding?: RegularViewTableItemData
) => {
	if (!event || !holding) {
		return undefined;
	}

	const formattedDate = formatBEDateToUiWithFallback(event.date, DATE_FORMATS.CALENDAR_DATE);
	const holdingDescription = `${holding.holderName}'s ${t('holdings.events.holding_desc')} ${holding.securityPersonName}:${holding.securityName}`;

	switch (event.type) {
		case HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES:
			return `${t('holdings.events.issue_desc')} ${holdingDescription} ${t('holdings.events.on_desc')} ${formattedDate}`;

		case HoldingEventType.PLEDGING_OF_SHARES:
			return `${holdingDescription} ${t('holdings.events.pledge_desc')} ${event.pledgeToString}${
				event.pledgeFromString
					? ` ${t('holdings.events.against_desc')} ${event.pledgeFromString}`
					: ''
			} ${t('holdings.events.on_desc')} ${formattedDate}`;

		case HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS:
			return `${t('holdings.events.rights_desc')} ${holdingDescription} ${t('holdings.events.assigned_desc')} ${event.assignedToString} ${t('holdings.events.on_desc')} ${formattedDate}`;

		default:
			return event.note || '';
	}
};

export const hasAssignmentEvent = (events: HoldingEventRes[] = []): boolean => {
	return events.some((event) => event.type === HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS);
};

export const hasPledgeEvent = (events: HoldingEventRes[] = []): boolean => {
	return events.some((event) => event.type === HoldingEventType.PLEDGING_OF_SHARES);
};
